import { round2, toHundredths } from "./money";

// Further Tax on items.
//
// "Further Tax Applicable" is a company setting (Yes / No, default No). When
// it is Yes, the company's items show a "Further Tax %" field (the item's
// existing `furtherTax`), which starts at 4% and is compulsory; the user can
// change the rate. For any other company the field is not shown and the item
// form leaves the stored value alone. The server enforces the same rule
// (backend src/services/furtherTax.js).

/** What the item's `furtherTax` field is called on screen. */
export const FURTHER_TAX_LABEL = "Further Tax %";

/** The rate the field starts at, in percent. */
export const DEFAULT_FURTHER_TAX_RATE = 4;

const isBlank = (value) =>
  value === null || value === undefined || !String(value).trim();

/**
 * True when the company's setting is Yes. Anything else - No, or a company
 * saved before the setting existed - is No.
 */
export const appliesFurtherTax = (company) =>
  company?.furtherTaxApplicable === "Yes";

/**
 * The field's error for this rate, or "" when it can be saved. Only called for
 * a company that applies Further Tax.
 */
export const furtherTaxError = (value) => {
  if (isBlank(value)) return `${FURTHER_TAX_LABEL} is required for this company`;
  const rate = Number(value);
  if (!Number.isFinite(rate) || rate < 0 || rate > 100) {
    return `${FURTHER_TAX_LABEL} must be between 0 and 100`;
  }
  return "";
};

/**
 * The field's value once the item's company changes from `from` to `to`: the
 * default rate when the field is only now appearing (whatever it held while
 * hidden was never shown), otherwise the value it already had.
 */
export const furtherTaxOnCompanyChange = (current, from, to) =>
  appliesFurtherTax(to) && !appliesFurtherTax(from)
    ? DEFAULT_FURTHER_TAX_RATE
    : current;

/**
 * The value an item opened for editing starts with: its saved rate, or the
 * default when its company applies Further Tax and it has none saved yet.
 */
export const initialFurtherTax = (saved, company) =>
  appliesFurtherTax(company) && isBlank(saved) ? DEFAULT_FURTHER_TAX_RATE : saved;

/* ------------------------------ On invoices ------------------------------ */
//
// An invoice line carries Further Tax only when BOTH hold:
//   - the invoice's company has "Further Tax Applicable" set to Yes, and
//   - the buyer is not Active on the Active Taxpayer List (ATL).
//
// It is the item's Further Tax % of the line's Amount (quantity x unit price),
// taken before anything else - the discount does not reduce it:
//
//   Amount          3 x 440.68 =  1,322.04
//   Further Tax     4%         =     52.88   <- on the Amount
//   Sales Tax       18%        =    237.97   <- on the Amount, as before
//   Discount                   -    100.00
//   Line Total                    1,512.89
//
// The line's `furtherTax` is what FBR receives for it (services/fbrPayload.js
// in the backend sends it as is). Lines of any other company are never
// touched, so their figures stay exactly as they were.

/** `rate` % of `amount`, to the paisa (a half paisa rounds up). */
export const furtherTaxAmountFor = (amount, rate) =>
  Math.round((toHundredths(amount) * toHundredths(rate)) / 10000) / 100;

/** The % an item charges: its saved rate, or 4% when it has none saved. */
export const itemFurtherTaxRate = (item) =>
  isBlank(item?.furtherTax)
    ? DEFAULT_FURTHER_TAX_RATE
    : Number(item.furtherTax) || 0;

/**
 * Whether the invoice's lines carry Further Tax, from its company and what is
 * known of the buyer's ATL status (`atl` as useSection236HRate reports it):
 *   managed  the company has the setting on, so this rule decides the
 *            Further Tax of its lines
 *   applies  ...and the buyer is known not to be Active on the ATL. A buyer
 *            with no NTN / CNIC, or whose status could not be found out, is
 *            not Active - the same reading the Section 236H rate uses.
 *   pending  ...and the buyer's status is not settled yet (being checked, or
 *            not asked for yet)
 */
export const invoiceFurtherTax = (company, atl) => {
  const managed = appliesFurtherTax(company);
  const known = !!atl?.source;
  return {
    managed,
    applies: managed && known && atl.status !== "Active",
    pending: managed && (!known || !!atl?.isChecking),
  };
};

/** The line under the invoice totals: whether Further Tax is charged, and why. */
export const furtherTaxNote = (furtherTax, atl) => {
  if (!furtherTax.managed) return "";
  if (furtherTax.pending) return "Checking the buyer's ATL status for Further Tax...";
  const charged = "Further Tax is charged on each item's Amount.";
  if (atl?.source === "none") {
    return `Buyer has no NTN / CNIC, so is not on the ATL - ${charged}`;
  }
  if (atl?.source === "unknown") {
    return `The buyer's ATL status could not be checked - ${charged}`;
  }
  const saved =
    atl?.source === "saved"
      ? "The ATL could not be checked just now, so the buyer's saved status is used. "
      : "";
  return furtherTax.applies
    ? `${saved}Buyer is not Active on the ATL - ${charged}`
    : `${saved}Buyer is Active on the ATL - no Further Tax.`;
};

// Set on a line whose Further Tax this rule has worked out, holding the % it
// used. Screen-side only: it is taken off before the invoice is sent.
const MARK = "furtherTaxRate";

/** True for a line whose Further Tax this rule has worked out. */
export const isFurtherTaxLine = (line) => line?.[MARK] !== undefined;

/** The % a line's Further Tax was worked out at, or null when unknown. */
export const lineFurtherTaxRate = (line) =>
  isFurtherTaxLine(line) ? line[MARK] : null;

/**
 * The line with its Further Tax worked out again.
 *
 * For a company with the setting on: the Further Tax is `rate` % of the
 * Amount when it applies (0 when not), and the total is Amount + Sales Tax -
 * Discount + Further Tax - the total the screens already work out, plus the
 * Further Tax. For any other company the line is returned untouched, unless it
 * still carries this rule's figure from a company picked before; that is taken
 * back out.
 */
export const withFurtherTax = (line, furtherTax, rate) => {
  const base = round2(
    (parseFloat(line?.valueSalesExcludingST) || 0) +
      (parseFloat(line?.salesTaxApplicable) || 0) -
      (parseFloat(line?.discount) || 0),
  );
  if (furtherTax.managed) {
    const usedRate = furtherTax.applies ? rate : 0;
    const amount = furtherTaxAmountFor(line?.valueSalesExcludingST, usedRate);
    return {
      ...line,
      [MARK]: usedRate,
      furtherTax: amount,
      totalValues: round2(base + amount),
    };
  }
  if (!isFurtherTaxLine(line)) return line;
  const { [MARK]: _rate, ...rest } = line;
  return { ...rest, furtherTax: 0, totalValues: base };
};

/**
 * `items` with every line `inScope` accepts brought up to date by
 * withFurtherTax, or null when none of them changes. A line with no item, or
 * whose totals were cleared (a new transaction type), waits until it is priced
 * again. `rateOf(line)` is the line's item's %.
 */
export const reconcileFurtherTax = (
  items,
  furtherTax,
  rateOf,
  inScope = () => true,
) => {
  let changed = false;
  const next = (items || []).map((line) => {
    if (
      !line?.itemId ||
      line.totalValues === "" ||
      line.totalValues === null ||
      line.totalValues === undefined ||
      !inScope(line)
    ) {
      return line;
    }
    const updated = withFurtherTax(line, furtherTax, rateOf(line));
    if (
      updated.furtherTax === line.furtherTax &&
      updated.totalValues === line.totalValues &&
      updated[MARK] === line[MARK]
    ) {
      return line;
    }
    changed = true;
    return updated;
  });
  return changed ? next : null;
};

/** The invoice as it is sent: the screen-side marks taken off its lines. */
export const withoutFurtherTaxMarks = (invoice) => ({
  ...invoice,
  items: (invoice?.items || []).map(({ [MARK]: _rate, ...line }) => line),
});
